/**
 * UpcomingHolidaysAlert — Dashboard banner (FEATURE #9).
 *
 * Polls `GET /api/restaurants/{id}/special-hours/upcoming?within_days=7` and
 * surfaces the next scheduled special date so the owner can review the
 * schedule before it kicks in. Hidden when nothing is upcoming.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CalendarDays, ArrowRight, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const UpcomingHolidaysAlert = ({ token, restaurantId }) => {
  const [next, setNext] = useState(null);

  useEffect(() => {
    if (!token || !restaurantId) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/api/restaurants/${restaurantId}/special-hours/upcoming?within_days=7`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (cancelled) return;
        const u = r.data?.upcoming || [];
        setNext(u[0] || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, restaurantId]);

  if (!next) return null;

  const targetIso = next.kind === 'single_day' ? next.date : next.start_date;
  const days = (() => {
    try {
      const target = new Date(`${targetIso}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return Math.max(0, Math.round((target - today) / 86400000));
    } catch { return null; }
  })();
  const niceDate = (() => {
    try {
      return new Date(`${targetIso}T00:00:00`).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric',
      });
    } catch { return targetIso; }
  })();

  return (
    <div
      data-testid="upcoming-holidays-alert"
      className="mb-5 flex items-center justify-between gap-4 p-4 rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/[0.06] via-yellow-500/[0.04] to-amber-500/[0.04]"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/35 flex items-center justify-center shrink-0">
          <CalendarDays className="w-4 h-4 text-amber-300" />
        </span>
        <div className="min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.18em] text-amber-300 font-bold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Upcoming Holiday Schedule
          </p>
          <p data-testid="upcoming-holidays-headline" className="text-[13.5px] text-white font-semibold truncate">
            {next.title} is scheduled
            {days === 0 ? ' today' : days === 1 ? ' tomorrow' : ` in ${days} days`}
            <span className="text-white/55 font-normal"> · {niceDate}</span>
          </p>
        </div>
      </div>
      <Link
        to="/onboarding/setup#setup-holiday-hours-block"
        data-testid="upcoming-holidays-cta"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 font-bold text-[12px] shrink-0 transition-all"
      >
        Review Hours
        <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
};

export default UpcomingHolidaysAlert;
